import React, { useState, useEffect } from 'react';

import { useFormikContext } from 'formik';
import { FormField, FormSubmit } from 'core/components/form';
import withDatePicker from 'core/components/with-date-picker';
import useOverridesTaskExtraFields from '../../../hooks/use-overrides-task-extra-fields';
import WarningCard from './warning-card';

import useStyles from '../generate-task-ui.styles';

const typeToFormField = {
  selection: 'select',
  shorttext: 'string',
  boolean: 'switch',
  integer: 'numeric',
  longtext: 'textarea'
};

const getType = (type) => {
  if (!typeToFormField[type]) {
    return type;
  }
  return typeToFormField[type];
};

/**
 * GenerateTaskForm component renders form fields for a given task.
 *
 * @component
 * @param {string} name - The name of the task.
 * @param {string} description - The description of the task.
 * @param {Object} parameters - The parameters required for the task.
 * @param {Function} onSubmit - The function to be called when the form is submitted.
 * @param {boolean} loading - Indicates whether the task is currently running.
 * @param {Object} error - The error object if there was an error during task execution.
 * @param {Object} response - The response object containing the task output. If task started a state machine, this will have the taskRunId.
 * @param {Object} validationSchema - The Yup validation schema for the form.
 * @param {boolean} disabled - Whether the form should be disabled.
 * @returns {JSX.Element} The rendered GenerateTaskUI component.
 */
const GenerateTaskForm = ({
  parameters,
  name,
  loading,
  runStarted,
  taskOutput,
  stateMachineOutput,
  disabled = false
}) => {
  const classes = useStyles();
  const { values } = useFormikContext();
  const [params, setParams] = useState(parameters);
  const keys = Object.keys(params);

  useEffect(() => {
    const extraParams = useOverridesTaskExtraFields(name, values, parameters);
    setParams(extraParams);
  }, [values.overrideAction]);

  return (
    <>
      {params &&
        keys.map((param, idx) => {
          const conditionalFieldProps = {
            ...(params[param].type !== 'affinity' && { options: params[param].options }),
            // task forms expect a value from the options list, so don't let autocompletes submit free text
            ...(params[param].type === 'autocomplete' && {
              allowUserInput: params[param].allowUserInput ?? false,
              freeSolo: params[param].freeSolo ?? false
            })
          };

          return (
            <FormField
              key={`${param}-${idx}`}
              name={param}
              label={params[param].name}
              type={getType(params[param].type)}
              description={params[param].description}
              xs={params[param].type === 'date' ? 4 : 12}
              defaultToEmpty={params[param].defaultToEmpty}
              mode="dark"
              templateUrl={params[param].templateUrl}
              fast={params[param].useFastField !== false}
              disabled={disabled}
              disableFuture={params[param].disableFuture ?? true}
              {...conditionalFieldProps}
            />
          );
        })}
      <WarningCard name={name} action={values.overrideAction} />
      <FormSubmit
        variant="contained"
        color="secondary"
        loading={loading || runStarted}
        disabled={loading || runStarted || taskOutput || stateMachineOutput || disabled}
        className={classes.submit}
      >
        Submit
      </FormSubmit>
    </>
  );
};

export default withDatePicker(GenerateTaskForm);
